import React from 'react';
import { createRoot } from 'react-dom/client';

import { Amplify } from 'aws-amplify';
import { initialize } from 'react-ga';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
//import { composeWithDevTools } from 'redux-devtools-extension';
import { composeWithDevTools} from '@redux-devtools/extension'
import { persistReducer, persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import storage from 'redux-persist/lib/storage';
import {thunk as thunkMiddleware} from 'redux-thunk';

import { sendGADimensions } from './actions/Users/UserAction';
import App from './App';
import config from './aws-exports';
import reducers from './reducers/index';
import registerSw from './registerServiceWorker';
import gtmService from './services/GTMService';

const persistConfig = {
  key: 'root',
  storage,
};
const persistedReducer = persistReducer(persistConfig, reducers);
export const store = createStore(persistedReducer, composeWithDevTools(applyMiddleware(thunkMiddleware)));

const initializeReactGA = (trackerCode) => {
  const state = store.getState();
  initialize(trackerCode);
  if (state.AppSession && (typeof state.AppSession.token !== 'string' || !state.AppSession.token)) {
    sendGADimensions();
  } else {
    const impersonating = !!state.Impersonate.impersonate.userPersonated;
    const { userDetails, activeCasDetails = {} } = state.AppSession;
    if (impersonating) {
      sendGADimensions(userDetails.userPrincipalId);
    } else {
      sendGADimensions(
        userDetails.userPrincipalId,
        activeCasDetails.name,
        activeCasDetails.clientName,
        userDetails.institutionName,
      );
    }
  }
};

const initializeGTM = (gtmId) => {
  try {
    // Initialize GTM with the provided container ID or fallback to config
    const success = gtmService.initialize(gtmId);
    
    if (success) {
      // Send initial user dimensions to GTM if user is logged in
      const state = store.getState();
      if (state.AppSession && state.AppSession.token && typeof state.AppSession.token === 'string') {
        const { userDetails, activeCasDetails = {} } = state.AppSession;
        const impersonating = !!state.Impersonate.impersonate.userPersonated;
        
        const userDimensions = {
          user_id: userDetails.userPrincipalId || 'unknown',
          user_role: userDetails.userRole || 'unknown',
          user_email: userDetails.email || 'unknown',
          user_name: `${userDetails.given_name || ''} ${userDetails.family_name || ''}`.trim() || 'unknown',
          user_cas_name: activeCasDetails.name || 'unknown',
          user_client_name: activeCasDetails.clientName || 'unknown',
          user_institution_name: userDetails.institutionName || 'unknown',
          is_impersonating: impersonating,
          last_active_date: new Date().toISOString().split('T')[0]
        };
        
        gtmService.trackUserDimensions(userDimensions);
      }
    }
  } catch (error) {
    console.error('GTM initialization error:', error);
  }
};

const persistor = persistStore(store);
if (window.location.pathname === '/analytics/ui/login' ||
  window.location.pathname === '/analytics/ui/signup') {
  persistor.purge();
}

(async () => {
  try {
    const response = await (await fetch('/analytics/rest/config')).json();
    if (!response.gaTrackerCode) {
      response.gaTrackerCode = ' ';
    }
    
    // Initialize GA4
    initializeReactGA(response.gaTrackerCode);
    
    // Initialize GTM - check for gtmId in response, fallback to config default
    const gtmId = response.gtmId || null;
    initializeGTM(gtmId);
    
    const aws = config.environmentVariables(response);
    Amplify.configure(aws);
  } catch (error) {
    console.error(error);
  }

  const container = document.getElementById('root');
  const root = createRoot(container);
  root.render(
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <App />
      </PersistGate>
    </Provider>);

  registerSw();
})();
